if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentMediaIndex = 0;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();

        if (this.controlsInitialized) return;
        this.controlsInitialized = true;
        this.container = this.querySelector('[role="document"]');
        this.dialog = this.querySelector('[role="dialog"]');

        this.createNavigationButtons();
        this.addEventListener('keydown', this.handleKeydown);
        this.container?.addEventListener('touchstart', this.handleTouchStart, {
          passive: true,
        });
        this.container?.addEventListener('touchend', this.handleTouchEnd, {
          passive: true,
        });
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const activeMedia =
          (mediaId ? this.getSlideByMediaId(mediaId) : null) ||
          this.getAvailableSlides()[0];

        if (!activeMedia) return;

        this.setActiveMedia(activeMedia, false);
      }

      createNavigationButtons() {
        if (!this.dialog || this.querySelector('[data-product-modal-prev]'))
          return;

        this.prevButton = this.createNavigationButton('previous');
        this.nextButton = this.createNavigationButton('next');
        this.dialog.append(this.prevButton, this.nextButton);
        this.updateNavigationButtons();
      }

      createNavigationButton(direction) {
        const button = document.createElement('button');
        const isPrevious = direction === 'previous';

        button.type = 'button';
        button.className = `product-media-modal__nav product-media-modal__nav--${isPrevious ? 'prev' : 'next'}`;
        button.setAttribute(
          `data-product-modal-${isPrevious ? 'prev' : 'next'}`,
          '',
        );
        button.setAttribute(
          'aria-label',
          `${isPrevious ? 'Previous' : 'Next'} media`,
        );
        button.innerHTML = `<span aria-hidden="true">${isPrevious ? '&lsaquo;' : '&rsaquo;'}</span>`;

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.showAdjacentMedia(isPrevious ? -1 : 1);
        });
        button.addEventListener('pointerup', (event) =>
          event.stopPropagation(),
        );

        return button;
      }

      getAvailableSlides() {
        if (!this.container) return [];

        const visibleGalleryMediaIds = this.getVisibleGalleryMediaIds();

        return Array.from(this.container.children).filter((slide) => {
          if (
            !slide.matches('[data-media-id]') &&
            !slide.querySelector('[data-media-id]')
          )
            return false;
          if (
            visibleGalleryMediaIds.size > 0 &&
            !visibleGalleryMediaIds.has(this.getSlideMediaId(slide))
          )
            return false;
          if (this.isDesktop() && slide.classList.contains('hide-on-pc'))
            return false;
          if (this.isDesktop() && slide.classList.contains('medium-hide'))
            return false;
          if (this.isDesktop() && slide.classList.contains('large-up-hide'))
            return false;
          if (!this.isDesktop() && slide.classList.contains('small-hide'))
            return false;
          if (slide.classList.contains('hidden-by-variant')) return false;

          return true;
        });
      }

      getVisibleGalleryMediaIds() {
        const mediaGallery = document.getElementById(
          `MediaGallery-${this.dataset.section}`,
        );
        const mediaList = mediaGallery?.querySelector('.product__media-list');

        if (!mediaList) return new Set();

        return new Set(
          Array.from(mediaList.children)
            .filter((item) => item.hasAttribute('data-media-id'))
            .filter((item) => !item.classList.contains('hidden-by-variant'))
            .filter((item) => {
              if (this.isDesktop()) {
                return (
                  !item.classList.contains('medium-hide') &&
                  !item.classList.contains('large-up-hide')
                );
              }

              return !item.classList.contains('small-hide');
            })
            .map((item) => this.normalizeGalleryMediaId(item.dataset.mediaId))
            .filter(Boolean),
        );
      }

      getSlideByMediaId(mediaId) {
        return this.getAvailableSlides().find((slide) => {
          return this.getSlideMediaId(slide) === mediaId;
        });
      }

      getSlideMediaId(slide) {
        const media = slide.matches('[data-media-id]')
          ? slide
          : slide.querySelector('[data-media-id]');

        return media?.getAttribute('data-media-id');
      }

      normalizeGalleryMediaId(mediaId) {
        if (!mediaId) return '';

        const sectionPrefix = `${this.dataset.section}-`;

        if (mediaId.startsWith(sectionPrefix))
          return mediaId.slice(sectionPrefix.length);

        return mediaId.split('-').pop();
      }

      setActiveMedia(activeMedia, animate = true) {
        if (!this.container) return;

        window.pauseAllMedia();

        const slides = this.getAvailableSlides();
        const index = slides.indexOf(activeMedia);

        Array.from(this.container.children).forEach((element) => {
          element.classList.remove('active');
        });

        activeMedia.classList.add('active');
        this.currentMediaIndex = index >= 0 ? index : 0;
        this.loadActiveDeferredMedia(activeMedia);
        this.scrollActiveMediaIntoView(activeMedia, animate);
        this.updateNavigationButtons();
      }

      showAdjacentMedia(direction) {
        const slides = this.getAvailableSlides();

        if (slides.length <= 1) return;

        const activeMedia = this.container.querySelector(':scope > .active');
        const activeIndex = slides.indexOf(activeMedia);
        const nextIndex =
          (activeIndex + direction + slides.length) % slides.length;

        this.setActiveMedia(slides[nextIndex], true);
      }

      scrollActiveMediaIntoView(activeMedia, animate) {
        activeMedia.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: animate ? 'smooth' : 'auto',
        });

        const matchedMedia = activeMedia.matches('[data-media-id]')
          ? activeMedia
          : activeMedia.querySelector('[data-media-id]');
        const mediaWidth =
          matchedMedia?.width ||
          matchedMedia?.clientWidth ||
          activeMedia.clientWidth;

        if (this.container && mediaWidth > this.container.clientWidth) {
          this.container.scrollLeft =
            (mediaWidth - this.container.clientWidth) / 2;
        }
      }

      loadActiveDeferredMedia(activeMedia) {
        const deferredMedia =
          activeMedia.nodeName == 'DEFERRED-MEDIA'
            ? activeMedia
            : activeMedia.querySelector('deferred-media');
        const activeMediaTemplate = deferredMedia?.querySelector('template');
        const activeMediaContent = activeMediaTemplate
          ? activeMediaTemplate.content
          : null;

        if (deferredMedia && activeMediaContent) deferredMedia.loadContent(false);
      }

      updateNavigationButtons() {
        const hasMultipleSlides = this.getAvailableSlides().length > 1;

        this.prevButton?.toggleAttribute('hidden', !hasMultipleSlides);
        this.nextButton?.toggleAttribute('hidden', !hasMultipleSlides);
      }

      handleKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showAdjacentMedia(-1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showAdjacentMedia(1);
        }
      }

      handleTouchStart(event) {
        const touch = event.changedTouches[0];

        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }

      handleTouchEnd(event) {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        const isHorizontalSwipe =
          Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;

        if (!isHorizontalSwipe) return;

        this.showAdjacentMedia(deltaX < 0 ? 1 : -1);
      }

      isDesktop() {
        return window.matchMedia('(min-width: 768px)').matches;
      }
    }
  );
}
